// The calendar screen's state, as URL search params.
//
// Same reasoning as `billingSearch.ts` and `regionsSearch.ts`: "December, just
// the term changes, for Harris" is a link somebody sends before a planning
// meeting, and a screen whose controls live only in component state cannot be
// one. Back walks the months as well as the filters.
//
// It follows the hardened shape those two settled on, for the same two reasons:
// every field carries its own `.catch(undefined)`, so ONE unreadable param falls
// back alone instead of taking the rest of the link with it; and the parse runs
// AGAIN in `searchToCalendarPage` at the point of use, because TanStack Router
// merges each route's validated search over its parent's and the root route
// validates nothing — so a `?month=2026-13` that this schema rejected still
// reaches the component, and without the second parse it would reach the API,
// where it is a 400 on a screen that should have shown this month.

import { z } from "zod";
import { CalendarEventKind, Currency, ReportScope, Uuid } from "@crm/shared";
import type { CalendarQueryState } from "../../api/queries.js";
import {
  addMonths,
  calendarMonthOf,
  formatIsoMonth,
  monthGridRange,
  parseCalendarMonth,
  todayIsoDate,
  type CalendarMonth,
} from "../../format/calendarDate.js";

const IsoMonthPattern = /^\d{4}-\d{2}$/;

/**
 * The month grid, or the same events as a chronological list.
 *
 * A view mode rather than a route of its own, exactly as the billing screen's
 * two views are: the month, the kind filter and the partner scope are all owned
 * here already, and a second route would either duplicate them or leave the
 * agenda unable to be filtered.
 */
export const CalendarViewMode = z.enum(["month", "agenda"]);
export type CalendarViewMode = z.infer<typeof CalendarViewMode>;

/** One kind or several, the same way the API spells the same filter. */
const KindFilter = z.union([CalendarEventKind, z.array(CalendarEventKind)]);

const CalendarSearchSchema = z.object({
  /** `YYYY-MM` — the visible month. Absent means the month containing today. */
  month: z.string().regex(IsoMonthPattern).optional().catch(undefined),
  view: CalendarViewMode.optional().catch(undefined),
  kind: KindFilter.optional().catch(undefined),
  /**
   * Validated as a uuid rather than passed through: `?partner_id=harris` is
   * `validation_failed` from the API, and the calendar is perfectly readable
   * without a partner filter — so the unreadable half is dropped and the rest of
   * the link still works.
   */
  partner_id: Uuid.optional().catch(undefined),
  scope: ReportScope.optional().catch(undefined),
});

export type CalendarSearch = z.infer<typeof CalendarSearchSchema>;

export function validateCalendarSearch(raw: Record<string, unknown>): CalendarSearch {
  const parsed = CalendarSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

export const DEFAULT_CALENDAR_VIEW: CalendarViewMode = "month";
export const DEFAULT_CALENDAR_SCOPE: ReportScope = "rolled_up";

/**
 * Everything the screen is. One object, so changing the kind filter cannot
 * navigate away the month, and paging the month cannot drop the filter.
 */
export interface CalendarPageState {
  month: CalendarMonth;
  view: CalendarViewMode;
  /** Empty means every kind — the same thing an absent filter means to the API. */
  kinds: readonly CalendarEventKind[];
  partnerId: string | null;
  scope: ReportScope;
}

/** The month containing `today`. The default, and the "Today" control's target. */
export function currentMonth(today: string = todayIsoDate()): CalendarMonth {
  // `today` comes from `todayIsoDate`, so the fallback is unreachable in
  // practice — but a default has to exist for the parse to stay total.
  return calendarMonthOf(today) ?? { year: 1970, month: 1 };
}

/** One kind or several, as one list, in the contract's own order. */
function kindsOf(raw: CalendarSearch["kind"]): readonly CalendarEventKind[] {
  if (raw === undefined) return [];
  const asked = Array.isArray(raw) ? raw : [raw];
  // De-duplicated and re-ordered, so `?kind=deal_start&kind=bill_due` and the
  // chips' own order produce the same URL and therefore the same query key.
  return CalendarEventKind.options.filter((kind) => asked.includes(kind));
}

export function searchToCalendarPage(
  raw: CalendarSearch,
  today: string = todayIsoDate(),
): CalendarPageState {
  const search = validateCalendarSearch(raw as Record<string, unknown>);
  const month = search.month === undefined ? null : parseCalendarMonth(search.month);
  return {
    month: month ?? currentMonth(today),
    view: search.view ?? DEFAULT_CALENDAR_VIEW,
    kinds: kindsOf(search.kind),
    partnerId: search.partner_id ?? null,
    scope: search.scope ?? DEFAULT_CALENDAR_SCOPE,
  };
}

/** Defaults omitted, so an unfiltered calendar on this month is a clean `/calendar`. */
export function calendarPageToSearch(
  state: CalendarPageState,
  today: string = todayIsoDate(),
): CalendarSearch {
  const month = formatIsoMonth(state.month);
  const isCurrentMonth = month === formatIsoMonth(currentMonth(today));

  return {
    ...(isCurrentMonth ? {} : { month }),
    ...(state.view === DEFAULT_CALENDAR_VIEW ? {} : { view: state.view }),
    // All seven kinds selected is the same request as none, and writing seven
    // params for it would make the default view an unshareably long URL.
    ...(state.kinds.length === 0 || state.kinds.length === CalendarEventKind.options.length
      ? {}
      : { kind: [...state.kinds] }),
    ...(state.partnerId === null ? {} : { partner_id: state.partnerId }),
    // The scope only means something alongside a partner, so writing it into an
    // all-partners URL would be two different links to one identical answer.
    ...(state.partnerId === null || state.scope === DEFAULT_CALENDAR_SCOPE
      ? {}
      : { scope: state.scope }),
  };
}

/** Step the calendar a month at a time. */
export function shiftMonth(state: CalendarPageState, delta: number): CalendarPageState {
  return { ...state, month: addMonths(state.month, delta) };
}

/**
 * Toggle one kind chip.
 *
 * "All seven" and "none" are the same request, so turning the last chip back on
 * collapses to the empty list rather than an explicit seven — otherwise the URL
 * and the query key for the unfiltered calendar would depend on how the reader
 * got there.
 */
export function toggleKind(state: CalendarPageState, kind: CalendarEventKind): CalendarPageState {
  const selected = state.kinds.length === 0 ? CalendarEventKind.options : state.kinds;
  const next = selected.includes(kind)
    ? selected.filter((value) => value !== kind)
    : CalendarEventKind.options.filter((value) => value === kind || selected.includes(value));

  return { ...state, kinds: next.length === CalendarEventKind.options.length ? [] : next };
}

/**
 * The query this state implies. The window is the month's FULL grid range —
 * leading and trailing cells included — because a cell that is drawn but not
 * queried is a cell that lies about being empty.
 *
 * The currency is the reader's report currency, not part of the page's state
 * (`format/reportCurrency.tsx`).
 */
export function calendarPageToQuery(
  state: CalendarPageState,
  currency: Currency,
): CalendarQueryState {
  const range = monthGridRange(state.month);
  return {
    from: range.from,
    to: range.to,
    currency,
    kinds: state.kinds,
    partnerId: state.partnerId,
    scope: state.scope,
  };
}
